/**
 * React Query hook for the Door times page. One endpoint:
 * GET /v1/stores/door-times?from=&to=&store_no= (capability stores.doortimes).
 * Same conventions as useStores.ts: 404 or 503 means "not deployed yet" and the
 * page shows a quiet placeholder.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiGet } from './api';
import { STORES_API_BASE } from './useStores';

export type DoorTimesRow = {
  store_no: string;
  store_name: string | null;
  date: string;
  weekday: number;
  /** The date is the store's own local today. */
  is_today: boolean;
  first_entry: string | null;
  last_entry: string | null;
  /** Latest person moving either direction. */
  last_crossing: string | null;
  /** Last person to leave, at any hour: normally the closing employee. */
  last_exit?: string | null;
  /** First and last register sale of the day, store-local. */
  first_sale?: string | null;
  last_sale?: string | null;
  entries: number;
  posted_open: string | null;
  posted_close: string | null;
  minutes_late: number | null;
  opened_late: boolean;
  last_entry_minutes_before_close: number | null;
  /** Null for the store's current local day: early close is judged only once the day is over. */
  minutes_early_close: number | null;
  closed_early: boolean;
};

export type DoorTimesResponse = {
  from: string;
  to: string;
  rows: DoorTimesRow[];
};

export function useDoorTimes(
  from: string,
  to: string,
  storeNo: string | null,
  enabled = true,
): UseQueryResult<DoorTimesResponse, Error> {
  return useQuery<DoorTimesResponse, Error>({
    queryKey: ['stores', 'door-times', from, to, storeNo ?? 'all'],
    queryFn: () => {
      const qs = new URLSearchParams({ from, to });
      if (storeNo) qs.set('store_no', storeNo);
      return apiGet<DoorTimesResponse>(`${STORES_API_BASE}/door-times?${qs.toString()}`);
    },
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}
